import { useEffect, useRef } from "react";
import type { LipSyncMorphTarget } from "./useLipSync";

// Liveness Group A — random microexpressions (brow raise, eye squint,
// cheek puff, nose sneer) every 8-15s. Each plays for ~600ms with a
// sinusoidal ease (0 → peak → 0). Always-on. Targets brow/eye/cheek/nose
// morphs that useLipSync never touches — no viseme path conflict.

const MICROEXPRESSIONS = [
  { morph: "browInnerUp",     peakWeight: 0.20 },
  { morph: "browDownLeft",    peakWeight: 0.15 },
  { morph: "browDownRight",   peakWeight: 0.15 },
  { morph: "eyeSquintLeft",   peakWeight: 0.15 },
  { morph: "eyeSquintRight",  peakWeight: 0.15 },
  { morph: "cheekPuff",       peakWeight: 0.10 },
  { morph: "noseSneerLeft",   peakWeight: 0.10 },
  { morph: "noseSneerRight",  peakWeight: 0.10 },
] as const;

const ALL_MICRO_MORPHS = MICROEXPRESSIONS.map((m) => m.morph);

export interface UseMicroexpressionOptions {
  morphTarget: LipSyncMorphTarget | null;
  enabled?: boolean;
  minIntervalMs?: number;
  maxIntervalMs?: number;
  durationMs?: number;
}

export function useMicroexpression({
  morphTarget,
  enabled = true,
  minIntervalMs = 8000,
  maxIntervalMs = 15000,
  durationMs = 600,
}: UseMicroexpressionOptions): void {
  const rafRef = useRef<number | null>(null);
  const expressionStartRef = useRef<number | null>(null);
  const currentExpressionRef = useRef<typeof MICROEXPRESSIONS[number] | null>(null);
  const nextAtRef = useRef<number>(0);

  useEffect(() => {
    if (!enabled || !morphTarget) return;

    const scheduleNext = (now: number) => {
      const interval = minIntervalMs + Math.random() * (maxIntervalMs - minIntervalMs);
      nextAtRef.current = now + interval;
    };

    scheduleNext(performance.now());

    const tick = () => {
      const now = performance.now();

      if (expressionStartRef.current !== null && currentExpressionRef.current) {
        const elapsed = now - expressionStartRef.current;
        const t = Math.min(elapsed / durationMs, 1);
        const easedWeight = currentExpressionRef.current.peakWeight * Math.sin(Math.PI * t);
        const idx = morphTarget.morphTargetDictionary[currentExpressionRef.current.morph];
        if (idx !== undefined) {
          morphTarget.morphTargetInfluences[idx] = easedWeight;
        }
        if (t >= 1) {
          if (idx !== undefined) morphTarget.morphTargetInfluences[idx] = 0;
          expressionStartRef.current = null;
          currentExpressionRef.current = null;
          scheduleNext(now);
        }
      } else if (now >= nextAtRef.current) {
        const next = MICROEXPRESSIONS[Math.floor(Math.random() * MICROEXPRESSIONS.length)];
        currentExpressionRef.current = next;
        expressionStartRef.current = now;
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      for (const morph of ALL_MICRO_MORPHS) {
        const idx = morphTarget.morphTargetDictionary[morph];
        if (idx === undefined) continue;
        morphTarget.morphTargetInfluences[idx] = 0;
      }
      expressionStartRef.current = null;
      currentExpressionRef.current = null;
    };
  }, [morphTarget, enabled, minIntervalMs, maxIntervalMs, durationMs]);
}
